import type * as THREE from 'three';
import type { BlackoutHallSeam, ColliderHandle, RoomCapabilities, RoomEnvironment, RoomModule, RoomModuleDefinition, RoomOptions } from '@/game/roomModule';
import { getZone } from '@/render/palette';
import {
  BLACKOUT_HALL_SPAWN, FATTENING_SPAWN, FATTENING_ZONE_ID, createFatteningHall, createOtto, fatteningAmbience, fatteningFixtures,
  fatteningSolids, ottoBodySolid,
} from '@/world/fattening';

/**
 * Beat 3's fattening hall, and beat 8's blackout in it, as a room module.
 * Until session 82 it was a construction branch in `main.ts`.
 *
 * **The room is static.** Otto's demonstration and roll are beat 3's
 * (`OTTO_PART_MODULE`), which resolves his rig out of `RoomModule.otto`; the fuse, the crates and the lights going out are beat
 * 8's (`BLACKOUT_HALL_PART_MODULE`), which takes the fuse box and the hall's
 * point lights from `RoomModule.blackoutHall`.
 *
 * `?dress=0` is not honoured, as the legacy branch never honoured it.
 */

/** Beat 8, part 2: the hall after Act 1, Otto's pen hosed out. */
function isBlackout(options: RoomOptions): boolean {
  return options.beat === 8 && options.part === 2;
}

function fatteningEnvironment(options: RoomOptions): RoomEnvironment {
  const zone = getZone(FATTENING_ZONE_ID);
  if (zone === undefined) throw new Error(`unknown zone "${FATTENING_ZONE_ID}"`);
  return {
    zone,
    lighting: {
      fixtures: fatteningFixtures(),
      // Almost no key, because it is night and there is no outside. What is left
      // of it is doing one job: a directional is the only shadow-caster in the
      // rig (point lights here cast none), so without it every shape in the room
      // loses its contact with the floor and the pens read as printed on it.
      keyDirection: { x: -0.28, y: 0.92, z: 0.27 },
      keyIntensity: 0.2,
      // The hall is 12 m across and 24 long. Covering the width and the middle
      // of the length is what the beat needs; the far end is past the haze.
      shadowExtent: 12,
      // Both floors are low, and they are the reason the gaps between the pools
      // are actually dark. A generous ambient in a night room is the single
      // easiest way to end up with a well-lit picture of a dark place.
      hemiIntensity: 0.12,
      ambientIntensity: 0.05,
      // Key + hemi + ambient is 0.37, and one high-bay delivers about 2.0 to the
      // floor beneath it. Anchoring at 2.2 puts what is under a lamp at the top of
      // the ramp and everything between two lamps near the bottom of it, which is
      // the entire staging. See ZoneLightingOptions.exposureReference for what
      // happens if a room with practicals leaves this at its default: the whole
      // shader appears not to work.
      exposureReference: 2.2,
    },
    background: { kind: 'rig' },
    spawn: isBlackout(options) ? BLACKOUT_HALL_SPAWN : FATTENING_SPAWN,
    ambience: fatteningAmbience(),
  };
}

function createFatteningRoom(options: RoomOptions, capabilities: RoomCapabilities): RoomModule {
  const blackout = isBlackout(options);
  const hallOptions = blackout ? { returnState: true } : {};
  const hall = createFatteningHall(hallOptions);
  capabilities.scene.add(hall);
  // Separately, because he has pivots and the dressing does not. See
  // `createOtto`. The blackout hall does not build him: his pen was hosed out
  // two beats ago.
  const otto: THREE.Group | null = blackout ? null : createOtto();
  if (otto !== null) capabilities.scene.add(otto);
  const colliders: ColliderHandle[] = fatteningSolids(hallOptions).map((solid) => capabilities.colliders.add(solid));
  if (otto !== null) {
    // ── Otto is solid ─────────────────────────────────────────────────────────
    //
    // **It is his STANDING pose and it does not cover the roll. Read this
    // before trusting it.** An earlier version of this comment claimed the box
    // was what kept her out of the volume he rolls through. It is not, and an
    // independent review caught it (D-147).
    //
    // The director rotates his render rig up to −2.75 rad about his long axis
    // and writes nothing back to simulation, so the collider never moves. His
    // hooves sit ~0.55 m from that axis against a standing half-width of about
    // 0.41 m, which means at ninety degrees of roll he sweeps roughly 0.14 m
    // further toward the alley than this box.
    //
    // What actually keeps her clear is height, not this collider: everything
    // that swings out laterally does so at pivot height — about 0.60 m — and
    // she is 0.32 m tall, so it passes over her. His barrel is on the axis and
    // therefore does not move at all, which is why the thing at her own height
    // stays put.
    //
    // **That is an argument, not a gate, and this project has shipped five of
    // those (D-100).** G-c in `docs/beat-3-revision-plan.md` is unwritten and
    // has to assert the clearance at her actual height across the whole nine
    // seconds, not against these half-extents. Until it exists, this box's job
    // is only the plain one: she cannot walk through a pig.
    colliders.push(capabilities.colliders.add(ottoBodySolid(otto)));
  }
  // Beat 8's fuse box, from the dressing the returned hall just built. The
  // lights are the rig's: `src/render/lighting.ts` is the only place a point
  // light is made, so these are the same lights, in the same order, that the
  // legacy branch found by traversing the whole scene.
  let blackoutHall: BlackoutHallSeam | null = null;
  if (blackout) {
    const fuseBox = hall.getObjectByName('blackout-fuse-box');
    if (!fuseBox) throw new Error('fattening (beat 8 / part 2): the returned hall lost its fuse box');
    blackoutHall = { fuseBox, lights: capabilities.rigPointLights };
  }
  let disposed = false;
  return {
    interactables: [],
    fixedStep(): void { /* static: beat 3's and beat 8's directors own everything that moves */ },
    renderStep(): void { /* static */ },
    framing: () => null,
    diagnostics: () => ({ kind: 'none' }),
    ...(otto === null ? {} : { otto }),
    ...(blackoutHall === null ? {} : { blackoutHall }),
    dispose(): void {
      if (disposed) return;
      disposed = true;
      for (const handle of colliders) capabilities.colliders.remove(handle);
      capabilities.scene.remove(hall);
      if (otto !== null) capabilities.scene.remove(otto);
    },
  };
}

export const FATTENING_ROOM_MODULE: RoomModuleDefinition = {
  room: 'fattening',
  environment: fatteningEnvironment,
  create: (options, capabilities) => createFatteningRoom(options, capabilities),
};
